import type { Logger } from "@expert/logging";

import { DraggableComponent } from "@expert/solve-tools";
import { Tabs } from "@mantine/core";

import { useSalesContext } from "../../../sales-context/SalesContext";
import { useGuidedBrowsingStore } from "../store/useGuidedBrowsingStore";
import styles from "./GuidedBrowsingModal.module.css";

interface GuidedBrowsingModalProps {
    sessionId: string;
    logger: Logger;
}

function GuidedBrowsingFrame({ src }: { src: string }) {
    return (
        <iframe
            className={styles.iframe}
            height="100%"
            width="100%"
            src={src}
            // allow-same-origin is required: without it, the framed page's origin is the opaque
            // "null", and every request it makes carries `Origin: null` — the adotcom platform's own
            // resource loading (osp/config/route-layouts, its module-federation JS chunks) gets
            // rejected by CORS as a result, so BOTH tabs fail to render at all, not just Live
            // Session. Confirmed by reproducing with allow-same-origin dropped (commit 2a057f9a1):
            // every first-party fetch the platform makes for itself failed with
            // "blocked by CORS policy: No 'Access-Control-Allow-Origin' header ... origin 'null'".
            // Do not drop it again without confirming this platform's asset loading still works.
            sandbox="allow-scripts allow-forms allow-same-origin"
            // Local Network Access permission defaults to 'self', so a cross-origin iframe is never
            // delegated it — the consent-relay page's own WebSocket connection to its internal-IP
            // relay host gets silently blocked (net::ERR_BLOCKED_BY_LOCAL_NETWORK_ACCESS_CHECKS)
            // before the socket ever opens, with no permission prompt shown. `local-network-access`
            // is Chromium's alias for the newer split `local-network`/`loopback-network` feature
            // names; listing both covers Chrome versions on either side of that rename. Confirmed
            // via chrome://net-export capture during the 2026-09-17 guided-browsing WS investigation.
            allow="local-network-access; local-network"
            title="guided-browsing-iframe"
            onContextMenu={(event) => event.preventDefault()}
        />
    );
}

export function GuidedBrowsingModal({ sessionId, logger }: GuidedBrowsingModalProps) {
    const { analytics, partner } = useSalesContext();
    const session = useGuidedBrowsingStore((state) => state.getSession(sessionId));
    const frameLocation = useGuidedBrowsingStore((state) => state.frameLocation);
    const frameSize = useGuidedBrowsingStore((state) => state.frameSize);
    // Actions are stable refs; read once without subscribing.
    const { closeModal, setFrameLocation, setFrameSize } = useGuidedBrowsingStore.getState();

    if (!session || session.deliveryStatus === "failed") {
        return null;
    }

    const handleTabChange = (value: string | null) => {
        if (value === "sessionUrl") {
            void analytics.dispatcher.dispatchBusinessEvent("GuidedBrowsingLiveSessionTabSelected", {
                sessionId,
                partner,
                deliveryMethod: session.deliveryMethod,
            });
        }
    };

    return (
        <DraggableComponent
            backgroundColor="var(--mantine-color-dark-5)"
            iconColor="var(--mantine-color-dark-0)"
            location={frameLocation}
            logger={logger}
            onClose={() => closeModal(sessionId)}
            opened={session.isModalOpen}
            setLocation={setFrameLocation}
            setSize={setFrameSize}
            size={frameSize}
            testId="guided-browsing"
            title="Guided Browsing"
        >
            {session.sessionUrl ? (
                <Tabs
                    // Remounts (resetting to defaultValue) whenever a resend delivers a new link —
                    // the modal stays mounted across sends, so without this the expert could stay
                    // parked on "Live Session" from a previous send and have the new one auto-load
                    // there before they've chosen it.
                    key={`${session.url}:${session.sessionUrl}`}
                    defaultValue="sessionUrl"
                    color="asurion"
                    keepMounted={false}
                    className={styles.tabs}
                    classNames={{ list: styles.tabList, panel: styles.panel }}
                    onChange={handleTabChange}
                >
                    <Tabs.List>
                        <Tabs.Tab value="url">Preview</Tabs.Tab>
                        <Tabs.Tab value="sessionUrl">Live Session</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="url">
                        <GuidedBrowsingFrame src={session.url} />
                    </Tabs.Panel>
                    <Tabs.Panel value="sessionUrl">
                        <GuidedBrowsingFrame src={session.sessionUrl} />
                    </Tabs.Panel>
                </Tabs>
            ) : (
                <GuidedBrowsingFrame src={session.url} />
            )}
        </DraggableComponent>
    );
}
